import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Plus, Trash2, ChevronDown, ChevronUp, Cloud, CloudOff, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getCitiesForState } from '../constants/indianCities';
import PefPdfCard from './PefPdfCard';
import {
  PROPERTY_STATUS_OPTIONS,
  LEAD_STAGE_OPTIONS,
  INDIAN_STATES,
  WEBSITE_AVAILABLE_OPTIONS,
  EXISTING_OTA_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  SETUP_PAYMENT_STATUS_OPTIONS,
  CHECKLIST_STATUS_OPTIONS,
  OTA_NAME_OPTIONS,
  getAvailableOTAOptions,
  getEmptyOTAItem,
  getEmptyPropertyChecklist,
  MAX_OTA_ENTRIES
} from '../constants/dropdownOptions';
import { useOtaOptions } from '../hooks/useOtaOptions';

// Phase 19.3 — Existing Partnership multi-select was removed per the
// Accounts spec. "Existing OTA Connected" is now derived from OTA
// Information rows whose OTA Status = "Self Manage" (see handleSubmit).

// Map an OTA value (e.g. "Booking_Com") to its display label (e.g.
// "Booking.com"). Used to derive "Existing OTA Connected" from Self-Manage
// OTA rows on save.
const OTA_NAME_LABELS = OTA_NAME_OPTIONS.reduce((m, o) => {
  if (o.value) m[o.value] = o.label;
  return m;
}, {});
const otaNameLabel = (val) => OTA_NAME_LABELS[val] || val || '';

// ── Locally-scoped option overrides for the Add Account popup ──
// (Do NOT mutate the shared dropdownOptions constants — they are imported
// by Properties, Settings, Imports, and the Superhost Admin Panel API.)
//
// Property Status: when creating an Account, only "New" is allowed.
const ACCOUNT_PROPERTY_STATUS_OPTIONS = [
  { value: 'New', label: 'New' },
];

// Account Type primary options. Manage unlocks a sub-type dropdown.
const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'Only Listing', label: 'Only Listing' },
  { value: 'Manage', label: 'Manage' },
];
const ACCOUNT_TYPE_MANAGE_SUBOPTIONS = [
  { value: '', label: 'Select' },
  { value: 'New Listing', label: 'New Listing' },
  { value: 'Integration', label: 'Integration' },
];

// OTA Status restricted to three values inside the Add Account popup.
// iter246 — Display label changed to "Self-Manage" (with hyphen) but the
// stored VALUE remains "Self Manage" for full backward compatibility with
// existing OTA rows and downstream self-manage matching logic.
const ACCOUNT_OTA_STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'Manage', label: 'Manage' },
  { value: 'Self Manage', label: 'Self-Manage' },
  { value: 'Integration', label: 'Integration' },
  // iter285 — "Integrated" is only selectable in the UI once both the Hotel
  // Link and Hotel ID are populated on the OTA row. See the <select> onChange
  // handler for the gating logic.
  { value: 'Integrated', label: 'Integrated' },
];

// iter246 — Maximum number of contact entries (primary + additional) per lead.
const MAX_CONTACTS = 5;

// Setup Clearing Terms. UI-only enum surfaced inside the Commercial Details
// section. Persisted at `payment_info.setup_clearing_terms`. Stored VALUES are
// unchanged ('Live Booking'/'Adjust') for full backward compatibility; only the
// display LABELS are updated (Bookings / EMI).
const SETUP_CLEARING_TERMS_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'Live Booking', label: 'Bookings' },
  { value: 'Adjust', label: 'EMI' },
];
// Map a stored Setup Clearing Terms value to its display label (handles legacy
// values + already-renamed inputs). Exported for reuse in read/edit views.
export const setupClearingTermsLabel = (value) => {
  const map = { 'Live Booking': 'Bookings', 'Adjust': 'EMI', 'Bookings': 'Bookings', 'EMI': 'EMI' };
  return map[value] || value || '-';
};

// Draft storage key
// NOTE: localStorage-based auto-save has been removed per the Accounts module
// spec. Drafts now persist server-side (one private draft per logged-in user)
// and are only written when the user explicitly clicks "Save Draft".

const ACCOUNT_DRAFT_API = `${process.env.REACT_APP_BACKEND_URL}/api/account-drafts/me`;

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="font-semibold text-gray-700">{title}</span>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

// Dropdown Select Component
const SelectField = ({ label, name, value, onChange, options, required = false, error }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      required={required}
      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${
        error ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
      }`}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && (
      <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid={`error-${name}`}>
        <AlertCircle size={12} /> {error}
      </p>
    )}
  </div>
);

const KNOWN_SOURCE_VALUES = LEAD_SOURCE_OPTIONS.filter(o => o.value && o.value !== '__other__').map(o => o.value);

const LeadSourceField = ({ value, onChange, required = false, error }) => {
  const isCustom = value && !KNOWN_SOURCE_VALUES.includes(value);
  const [showOtherInput, setShowOtherInput] = useState(isCustom);
  const [customText, setCustomText] = useState(isCustom ? value : '');

  // Update state when value prop changes (e.g., when editing a lead with custom source)
  useEffect(() => {
    const newIsCustom = value && !KNOWN_SOURCE_VALUES.includes(value);
    setShowOtherInput(newIsCustom);
    setCustomText(newIsCustom ? value : '');
  }, [value]);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === '__other__') {
      setShowOtherInput(true);
      setCustomText('');
      onChange('');
    } else {
      setShowOtherInput(false);
      setCustomText('');
      onChange(v);
    }
  };

  const handleCustom = (e) => {
    const v = e.target.value;
    setCustomText(v);
    onChange(v);
  };

  const dropdownValue = showOtherInput ? '__other__' : (KNOWN_SOURCE_VALUES.includes(value) ? value : '');

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Lead Source {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={dropdownValue}
        onChange={handleSelect}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${
          error ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
        }`}
        data-testid="lead-source-select"
      >
        {LEAD_SOURCE_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {showOtherInput && (
        <input
          type="text"
          value={customText}
          onChange={handleCustom}
          placeholder="Enter Lead Source"
          className={`w-full mt-2 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
            error ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
          }`}
          data-testid="lead-source-custom-input"
        />
      )}
      {error && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-source">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
};

// ── Searchable City dropdown ────────────────────────────────────────────
// Populated from the selected state (via getCitiesForState). An "Others"
// option at the bottom flips into a free-text input so users can enter a
// city we don't list.
//
// Backend field name remains `city` — this component is a UI-only
// enhancement and does NOT touch the lead payload schema.
const CityField = ({ state, value, onChange }) => {
  const baseCities = getCitiesForState(state);
  // Phase 18a — merge user-persisted custom cities (per state) from the
  // backend so previously-typed "Others" cities surface as suggestions.
  const [customCities, setCustomCities] = useState([]);
  useEffect(() => {
    if (!state) { setCustomCities([]); return; }
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const ctrl = new AbortController();
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/settings/custom-cities?state=${encodeURIComponent(state)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: ctrl.signal },
    )
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        if (!Array.isArray(rows)) { setCustomCities([]); return; }
        const names = rows
          .map(r => (r && typeof r.name === 'string' ? r.name.trim() : ''))
          .filter(Boolean);
        setCustomCities(names);
      })
      .catch(() => setCustomCities([]));
    return () => ctrl.abort();
  }, [state]);

  // Merge + de-dupe (case-insensitive); base list order preserved, custom
  // cities appended at the end so static options stay where users expect.
  const cities = (() => {
    const seen = new Set(baseCities.map(c => c.toLowerCase()));
    const merged = [...baseCities];
    for (const c of customCities) {
      if (!seen.has(c.toLowerCase())) {
        merged.push(c);
        seen.add(c.toLowerCase());
      }
    }
    return merged;
  })();
  // A value that matches no preset city (and isn't blank) is treated as
  // a previously-entered "Others" free-text city, so the input stays
  // editable when the user re-opens an existing record.
  const isPresetMatch = !!value && cities.includes(value);
  const [otherMode, setOtherMode] = useState(!!value && !isPresetMatch);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  // Reset other-mode when state changes — keep value in sync.
  useEffect(() => {
    const matches = state && getCitiesForState(state).includes(value);
    setOtherMode(!!value && !matches);
  }, [state, value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = cities.filter(c => c.toLowerCase().includes((query || '').toLowerCase()));

  if (otherMode) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Enter City Name <span className="text-red-500">*</span></label>
        <div className="flex gap-2">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your city"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            data-testid="account-city-other-input"
          />
          <button
            type="button"
            onClick={() => { setOtherMode(false); onChange(''); }}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
            data-testid="account-city-back-to-dropdown"
          >
            Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? query : (value || '')}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { setQuery(''); setIsOpen(true); }}
          placeholder={state ? 'Type to search…' : 'Select a state first'}
          disabled={!state}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${state ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'}`}
          data-testid="account-city-search-input"
        />
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        {isOpen && state && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto"
               data-testid="account-city-dropdown-list">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No matching cities</div>
            )}
            {filtered.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setIsOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${value === c ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}
                data-testid={`account-city-option-${c}`}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setOtherMode(true); onChange(''); setIsOpen(false); setQuery(''); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-gray-100 hover:bg-amber-50 text-amber-700 font-medium"
              data-testid="account-city-option-others"
            >
              Others
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Searchable State dropdown ───────────────────────────────────────────
// UI-only enhancement (backend field name stays `state`). Mirrors the
// CityField search pattern so both dropdowns feel consistent. Calls
// onChange with an event-like payload so the existing `handleChange`
// (which resets a stale city when the state changes) keeps working.
const StateField = ({ value, onChange, error }) => {
  const states = INDIAN_STATES.filter((o) => o.value).map((o) => o.value);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = states.filter((s) => s.toLowerCase().includes((query || '').toLowerCase()));

  return (
    <div ref={wrapRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? query : (value || '')}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { setQuery(''); setIsOpen(true); }}
          placeholder="Type to search…"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${
            error ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
          }`}
          data-testid="account-state-search-input"
        />
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        {isOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto"
               data-testid="account-state-dropdown-list">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No matching states</div>
            )}
            {filtered.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { onChange({ target: { name: 'state', value: s } }); setIsOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${value === s ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}
                data-testid={`account-state-option-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LeadModal = ({ isOpen, onClose, onSave, lead, initialDraft = null }) => {
  const { user: authUser } = useAuth();
  const isNewLead = !lead; // Track if creating new lead

  // Iter239 — dynamic OTA dropdown source. `historicalValues` keeps any
  // saved OTA name that has since been deleted from the config still
  // visible when editing an existing account.
  const historicalOtaValues = React.useMemo(
    () => (lead?.ota_info || []).map(o => o.ota_name).filter(Boolean),
    [lead]
  );
  const { options: dynamicOtaOptions } = useOtaOptions(historicalOtaValues);

  const [formData, setFormData] = useState({
    lead_owner: '',
    lead_date: new Date().toISOString().split('T')[0],
    hotel_name: '',
    owner_name: '',
    email: '',
    phone: '',
    // iter246 — additive multi-contact fields; primary stays in phone/email.
    additional_phones: [],
    additional_emails: [],
    state: '',
    city: '',
    country: 'India',
    lead_stage: 'Closed Won',
    pincode: '',
    source: '',
    // Hidden auto-defaults — kept to preserve existing backend lifecycle.
    property_status: isNewLead ? 'New' : 'Active',
    existing_ota_connected: '',
    ota_listing_link: '',
    address: '',
    no_of_rooms: '',
    payment_info: {
      interested_plan: '',
      payment_completed_date: '',
      one_time_setup_fee: '',
      token_advance: '',
      pending_payment: '',
      setup_payment_status: '',
      finalize_setup_token: '',
      commission_agreed: '',
      // Phase 18a — Setup Clearing Terms (Live Booking | Adjust).
      setup_clearing_terms: '',
    },
    property_checklist_info: getEmptyPropertyChecklist(),
    ota_info: [],
    custom_fields: {},
  });
  
  // Phase 18a.1 — File staged inside the modal during Add Account flow.
  // Uploaded by the parent (Leads.js) after the lead is created so the
  // backend endpoint (which requires lead_id) is satisfied.
  const [stagedPefFile, setStagedPefFile] = useState(null);
  
  const [draftStatus, setDraftStatus] = useState(''); // 'saving', 'saved', ''
  const [isSaving, setIsSaving] = useState(false); // Save Lead in-flight guard
  const [otaErrors, setOtaErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // Plans loaded from /api/account-plans for the Interested Plan dropdown.
  // Source of truth = Settings → Module Settings → Hotel Onboarded → Plan Management.
  const [accountPlans, setAccountPlans] = useState([]);
  const draftTimeoutRef = useRef(null);
  const formRef = useRef(null);

  // Load plans once when the modal opens. Tiny payload (<1KB), so no caching.
  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account-plans`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setAccountPlans(Array.isArray(d?.data) ? d.data : []))
      .catch(() => setAccountPlans([]));
  }, [isOpen]);

  // Auto-fill Lead Owner with the currently logged-in CRM user when
  // creating a new Account. We do NOT overwrite an existing value (so
  // edit-flow keeps whatever the lead was created with) and we do NOT
  // change any auth / role-management logic.
  useEffect(() => {
    if (isOpen && isNewLead && authUser && !formData.lead_owner) {
      const owner = authUser.name || authUser.email || '';
      if (owner) setFormData(prev => ({ ...prev, lead_owner: owner }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isNewLead, authUser]);

  // Mandatory fields for Basic Information
  const MANDATORY_FIELDS = {
    hotel_name: 'Property Name',
    owner_name: 'Owner/Contact Name',
    phone: 'Phone Number',
    state: 'State',
    city: 'City',
    lead_owner: 'Lead Owner',
    source: 'Lead Source',
  };

  // ── Server-backed draft helpers ──────────────────────────────────────
  // We deliberately do NOT auto-load or auto-save here. Auto-save was the
  // source of cross-user draft leakage in the previous implementation.
  // Drafts are only persisted on an explicit "Save Draft" click, and only
  // loaded when the parent (Leads page) chooses to pre-fill via the
  // `initialDraft` prop after the user picks "Draft" in the Add Account
  // chooser. See backend: routes/account_drafts.py
  const _authHeader = useCallback(() => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const saveDraft = useCallback(async () => {
    if (!isNewLead) return;
    setDraftStatus('saving');
    try {
      const res = await fetch(ACCOUNT_DRAFT_API, {
        method: 'PUT',
        headers: { ..._authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: formData }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDraftStatus('saved');
      setTimeout(() => setDraftStatus(''), 2500);
    } catch (e) {
      setDraftStatus('error');
      setTimeout(() => setDraftStatus(''), 3000);
    }
  }, [formData, isNewLead, _authHeader]);

  const clearDraft = useCallback(async () => {
    setDraftStatus('');
    try {
      await fetch(ACCOUNT_DRAFT_API, { method: 'DELETE', headers: _authHeader() });
    } catch {
      /* network error is benign — backend lifecycle is idempotent */
    }
  }, [_authHeader]);

  useEffect(() => {
    if (lead) {
      setFormData({
        lead_owner: lead.lead_owner || (authUser?.name || authUser?.email || ''),
        lead_date: lead.lead_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        hotel_name: lead.hotel_name || '',
        owner_name: lead.owner_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        additional_phones: Array.isArray(lead.additional_phones) ? lead.additional_phones : [],
        additional_emails: Array.isArray(lead.additional_emails) ? lead.additional_emails : [],
        state: lead.state || '',
        city: lead.city || '',
        country: lead.country || 'India',
        lead_stage: lead.lead_stage || 'Closed Won',
        pincode: lead.pincode || '',
        source: lead.source || '',
        property_status: lead.property_status || 'Active',
        existing_ota_connected: lead.existing_ota_connected || '',
        ota_listing_link: lead.ota_listing_link || '',
        address: lead.address || '',
        no_of_rooms: lead.no_of_rooms || '',
        no_of_properties: lead.no_of_properties ?? '',
        payment_info: {
          interested_plan: lead.payment_info?.interested_plan || '',
          payment_completed_date: lead.payment_info?.payment_completed_date || '',
          one_time_setup_fee: lead.payment_info?.one_time_setup_fee ?? '',
          token_advance: lead.payment_info?.token_advance ?? '',
          pending_payment: lead.payment_info?.pending_payment ?? '',
          setup_payment_status: lead.payment_info?.setup_payment_status || '',
          finalize_setup_token: lead.payment_info?.finalize_setup_token || '',
          ...lead.payment_info
        },
        property_checklist_info: (() => {
          // iter277 — Historical imports normalized checklist values to
          // "Completed" (with a trailing 'd'), and some sources uppercase
          // the value to "COMPLETED". Both mismatch the frontend dropdown
          // "Complete" and cause the field to fall back to the empty
          // option (visually indistinguishable from "Pending"). Translate
          // any case-insensitive "completed" → "Complete" on read so the
          // dropdown pre-fills correctly. Any other value passes through
          // untouched — no silent semantic rewrites.
          const src = lead.property_checklist_info || {};
          const healed = {};
          for (const [k, v] of Object.entries(src)) {
            const norm = typeof v === 'string' ? v.trim().toLowerCase() : v;
            healed[k] = (norm === 'completed') ? 'Complete' : v;
          }
          return { ...getEmptyPropertyChecklist(), ...healed };
        })(),
        ota_info: lead.ota_info || [],
        custom_fields: lead.custom_fields || {},
      });
    } else {
      // New account flow. The parent (Leads page) decides whether the user
      // continues a previously saved server draft (via `initialDraft`) or
      // starts blank. We never auto-load drafts from storage here.
      if (initialDraft && typeof initialDraft === 'object') {
        setFormData(prev => ({
          ...prev,
          ...initialDraft,
          payment_info: {
            ...(prev.payment_info || {}),
            ...(initialDraft.payment_info || {}),
          },
          property_checklist_info: {
            ...getEmptyPropertyChecklist(),
            ...(initialDraft.property_checklist_info || {}),
          },
          ota_info: Array.isArray(initialDraft.ota_info) ? initialDraft.ota_info : [],
        }));
        setDraftStatus('restored');
        setTimeout(() => setDraftStatus(''), 2500);
      } else {
        setFormData({
          lead_owner: authUser?.name || authUser?.email || '',
          lead_date: new Date().toISOString().split('T')[0],
          hotel_name: '',
          owner_name: '',
          email: '',
          phone: '',
          additional_phones: [],
          additional_emails: [],
          state: '',
          city: '',
          country: 'India',
          lead_stage: 'Closed Won',
          pincode: '',
          source: '',
          property_status: 'New',
          existing_ota_connected: '',
          ota_listing_link: '',
          address: '',
          no_of_rooms: '',
          payment_info: {
            interested_plan: '',
            setup_payment_status: '',
            setup_clearing_terms: '',
          },
          property_checklist_info: getEmptyPropertyChecklist(),
          ota_info: [],
          custom_fields: {},
        });
      }
    }
  }, [lead, isOpen, initialDraft]);

  const validateMandatoryFields = (data) => {
    const errors = {};
    Object.entries(MANDATORY_FIELDS).forEach(([field, label]) => {
      const value = data[field];
      if (!value || (typeof value === 'string' && value.trim() === '') || value === 'Select') {
        errors[field] = `${label} is required`;
      }
    });
    return errors;
  };

  const validateField = (name, value) => {
    if (MANDATORY_FIELDS[name]) {
      if (!value || (typeof value === 'string' && value.trim() === '') || value === 'Select') {
        return `${MANDATORY_FIELDS[name]} is required`;
      }
    }
    return null;
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return; // ignore double/triple clicks while saving
    setSubmitted(true);

    // Validate mandatory fields
    const errors = validateMandatoryFields(formData);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Phase 14.3 — explicit toast so the user can't miss why the
      // save did not go through. Previously only an inline banner was
      // shown, which users frequently missed → they thought save
      // worked and went hunting for the missing hotel in Lifecycle.
      const labels = Object.keys(errors).map(k => MANDATORY_FIELDS[k] || k).join(', ');
      try {
        toast.error(`Cannot save — missing: ${labels}`, {
          description: 'Fill the highlighted fields and try again.',
        });
      } catch (_) {
        // toast is best-effort
      }
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const el = formRef.current?.querySelector(`[name="${firstErrorField}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }

    // Listing-URL validation removed per the Accounts module spec — the OTA
    // section now exposes only Name / Status / (conditional) Hotel ID. The
    // backend payload still carries `listing_url` for legacy records but
    // it is no longer captured on the Add Account form.
    setOtaErrors([]);
    // Clear draft after successful submission
    clearDraft();
    // Sanitize data: convert empty strings to null for numeric fields
    const sanitized = { ...formData };
    if (sanitized.no_of_rooms === '' || sanitized.no_of_rooms === undefined) sanitized.no_of_rooms = null;
    if (sanitized.no_of_properties === '' || sanitized.no_of_properties === undefined) sanitized.no_of_properties = null;
    // Filter out empty OTA entries
    if (sanitized.ota_info) {
      sanitized.ota_info = sanitized.ota_info.filter(ota => ota.ota_name && ota.ota_name.trim() !== '');
    }
    // iter246 — normalise multi-contact arrays. Drop empty rows; trim.
    // The Primary contact is whatever is currently in `phone`/`email`
    // (the UI swaps the array entry into the primary slot when the user
    // marks a different row as Primary — see setPrimaryPhone/setPrimaryEmail).
    if (Array.isArray(sanitized.additional_phones)) {
      sanitized.additional_phones = sanitized.additional_phones
        .map(p => (p || '').trim())
        .filter(Boolean)
        .slice(0, MAX_CONTACTS - 1);
    }
    if (Array.isArray(sanitized.additional_emails)) {
      sanitized.additional_emails = sanitized.additional_emails
        .map(e => (e || '').trim())
        .filter(Boolean)
        .slice(0, MAX_CONTACTS - 1);
    }
    // "Existing OTA Connected" is derived from OTA rows with Status =
    // "Self Manage" (hotel already runs these itself). Stored as a
    // comma-joined label string in the existing `existing_ota_connected`
    // field (no schema change). These OTAs are excluded from CX/QC/listing
    // workflows on the backend.
    //
    // iter286 — Bugfix: only OVERWRITE `existing_ota_connected` when the
    // user actually has at least one Self-Manage OTA row. Previously we
    // blanked the field whenever the OTA list didn't contain a Self-Manage
    // row, which silently wiped any pre-existing value the user had set
    // (e.g. "Online" / "Offline" / "Partially Online") — and made it look
    // like adding an OTA link auto-flipped the account into "Self Manage".
    const selfManaged = (sanitized.ota_info || []).filter(
      o => (o.ota_status || '').trim().toLowerCase() === 'self manage' && o.ota_name && o.ota_name.trim()
    );
    if (selfManaged.length > 0) {
      sanitized.existing_ota_connected = selfManaged.map(o => otaNameLabel(o.ota_name)).join(', ');
    }
    // else: leave the user's previously-saved `existing_ota_connected`
    // value untouched.
    // Phase 18a.1 — surface the staged PEF file to the parent so it can
    // upload after the lead is created (the backend upload endpoint
    // requires a lead_id). Attached on a private key so it never leaks
    // into the JSON payload to /api/leads.
    if (stagedPefFile) {
      sanitized._pendingPefFile = stagedPefFile;
    }
    onSave(sanitized);
  };

  const handleClose = () => {
    // Closing without saving leaves any previously saved draft intact.
    // We never write here — drafts are written only on explicit Save Draft.
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // When state changes, drop a stale city that no longer belongs to
      // the newly-selected state. Existing state-selection logic itself
      // is untouched — we only nudge city in lock-step.
      if (name === 'state' && prev.city) {
        const cities = getCitiesForState(value);
        if (!cities.includes(prev.city)) next.city = '';
      }
      return next;
    });
    // Real-time validation: clear error when field is corrected
    if (submitted || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => {
        const next = { ...prev };
        if (error) next[name] = error;
        else delete next[name];
        return next;
      });
    }
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleOTAChange = (index, field, value) => {
    const updatedOTAInfo = [...formData.ota_info];
    updatedOTAInfo[index] = { ...updatedOTAInfo[index], [field]: value };
    setFormData(prev => ({ ...prev, ota_info: updatedOTAInfo }));
  };

  // iter259 — OTA checklist helpers. The OTA Information section now
  // renders every available OTA as a checkbox. Ticking an OTA appends an
  // ota_info row for it; unticking removes the row. `ota_status` (and
  // Hotel ID when status = Integration) are edited per-checked OTA.
  const toggleOtaByName = (otaName) => {
    setFormData(prev => {
      const existingIndex = (prev.ota_info || []).findIndex(o => o.ota_name === otaName);
      if (existingIndex >= 0) {
        // Untick: remove that OTA row.
        return { ...prev, ota_info: prev.ota_info.filter((_, i) => i !== existingIndex) };
      }
      // Tick: add a fresh row for this OTA (respect MAX_OTA_ENTRIES).
      if ((prev.ota_info || []).length >= MAX_OTA_ENTRIES) return prev;
      return {
        ...prev,
        ota_info: [...(prev.ota_info || []), { ...getEmptyOTAItem(), ota_name: otaName }],
      };
    });
  };

  const updateOtaFieldByName = (otaName, field, value) => {
    setFormData(prev => {
      const list = [...(prev.ota_info || [])];
      const idx = list.findIndex(o => o.ota_name === otaName);
      if (idx < 0) return prev;
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, ota_info: list };
    });
  };

  // ── iter246 — Multi-contact helpers ─────────────────────────────────
  // Row 0 = primary (backed by formData.phone / formData.email).
  // Rows 1..N = additional_phones / additional_emails entries.
  const addPhoneRow = () => {
    setFormData(prev => {
      const list = Array.isArray(prev.additional_phones) ? prev.additional_phones : [];
      if (list.length >= MAX_CONTACTS - 1) return prev;
      return { ...prev, additional_phones: [...list, ''] };
    });
  };
  const removePhoneRow = (idx) => {
    // idx is index into additional_phones (never removes the primary)
    setFormData(prev => ({
      ...prev,
      additional_phones: (prev.additional_phones || []).filter((_, i) => i !== idx),
    }));
  };
  const updateAdditionalPhone = (idx, value) => {
    setFormData(prev => {
      const list = [...(prev.additional_phones || [])];
      list[idx] = value;
      return { ...prev, additional_phones: list };
    });
  };
  // Swap primary phone with an additional phone at index idx.
  const setPrimaryPhoneFromAdditional = (idx) => {
    setFormData(prev => {
      const list = [...(prev.additional_phones || [])];
      const chosen = list[idx];
      list[idx] = prev.phone || '';
      return { ...prev, phone: chosen || '', additional_phones: list };
    });
  };

  const addEmailRow = () => {
    setFormData(prev => {
      const list = Array.isArray(prev.additional_emails) ? prev.additional_emails : [];
      if (list.length >= MAX_CONTACTS - 1) return prev;
      return { ...prev, additional_emails: [...list, ''] };
    });
  };
  const removeEmailRow = (idx) => {
    setFormData(prev => ({
      ...prev,
      additional_emails: (prev.additional_emails || []).filter((_, i) => i !== idx),
    }));
  };
  const updateAdditionalEmail = (idx, value) => {
    setFormData(prev => {
      const list = [...(prev.additional_emails || [])];
      list[idx] = value;
      return { ...prev, additional_emails: list };
    });
  };
  const setPrimaryEmailFromAdditional = (idx) => {
    setFormData(prev => {
      const list = [...(prev.additional_emails || [])];
      const chosen = list[idx];
      list[idx] = prev.email || '';
      return { ...prev, email: chosen || '', additional_emails: list };
    });
  };

  const addOTAEntry = () => {
    if (formData.ota_info.length < MAX_OTA_ENTRIES) {
      setFormData(prev => ({
        ...prev,
        ota_info: [...prev.ota_info, getEmptyOTAItem()]
      }));
    }
  };

  const removeOTAEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      ota_info: prev.ota_info.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-800">
              {lead ? 'Edit Lead' : 'Create New Account'}
            </h2>
            {/* Draft Status Indicator */}
            {isNewLead && draftStatus && (
              <span className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                draftStatus === 'saving' ? 'bg-yellow-100 text-yellow-700' :
                draftStatus === 'saved' ? 'bg-green-100 text-green-700' :
                draftStatus === 'restored' ? 'bg-blue-100 text-blue-700' : ''
              }`}>
                {draftStatus === 'saving' && <><CloudOff size={12} /> Saving...</>}
                {draftStatus === 'saved' && <><Cloud size={12} /> Draft saved</>}
                {draftStatus === 'restored' && <><Cloud size={12} /> Draft restored</>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleClose}
              data-testid="lead-modal-close"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} ref={formRef} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Phase 18a — Property Details (property attributes only) */}
          <CollapsibleSection title="Property Details" defaultOpen={true}>
            {Object.keys(fieldErrors).length > 0 && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="validation-error-banner">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-700 text-sm font-medium">Missing required field{Object.keys(fieldErrors).length > 1 ? 's' : ''}:</p>
                    <ul className="text-red-600 text-sm mt-1 list-disc ml-5" data-testid="validation-missing-list">
                      {Object.entries(fieldErrors).map(([fname, msg]) => (
                        <li key={fname} data-testid={`validation-missing-${fname}`}>{(MANDATORY_FIELDS[fname] || fname)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name <span className="text-red-500">*</span></label>
                <input
                  required
                  type="text"
                  name="hotel_name"
                  value={formData.hotel_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                    fieldErrors.hotel_name ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g. Grand Plaza Hotel"
                  data-testid="hotel-name-input"
                />
                {fieldErrors.hotel_name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-hotel_name">
                    <AlertCircle size={12} /> {fieldErrors.hotel_name}
                  </p>
                )}
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="address-input"
                />
              </div>

              <div className={fieldErrors.state ? 'has-error' : ''}>
                <StateField
                  value={formData.state}
                  onChange={handleChange}
                  error={fieldErrors.state}
                />
                {fieldErrors.state && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-state">
                    <AlertCircle size={12} /> {fieldErrors.state}
                  </p>
                )}
              </div>

              <div className={fieldErrors.city ? 'has-error' : ''}>
                <CityField
                  state={formData.state}
                  value={formData.city}
                  onChange={(val) => setFormData(prev => ({ ...prev, city: val }))}
                />
                {fieldErrors.city && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-city">
                    <AlertCircle size={12} /> {fieldErrors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="pincode-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country || ''}
                  onChange={handleChange}
                  placeholder="e.g. India"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="country-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Rooms</label>
                <input
                  type="number"
                  min="0"
                  name="no_of_rooms"
                  value={formData.no_of_rooms}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="total-rooms-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No of Properties</label>
                <input
                  type="number"
                  min="0"
                  name="no_of_properties"
                  value={formData.no_of_properties ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="no-of-properties-input"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Phase 18a — Owner Details (owner contact + sales pipeline) */}
          <CollapsibleSection title="Owner Details" defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="owner_name"
                  value={formData.owner_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                    fieldErrors.owner_name ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  data-testid="owner-name-input"
                />
                {fieldErrors.owner_name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-owner_name">
                    <AlertCircle size={12} /> {fieldErrors.owner_name}
                  </p>
                )}
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  {/* Primary row (backed by formData.phone) */}
                  <div className="flex items-center gap-2" data-testid="phone-row-primary">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                        fieldErrors.phone ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      data-testid="phone-input"
                      placeholder="Primary mobile"
                    />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 select-none" title="Primary Mobile">
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        className="w-4 h-4 accent-teal-600 cursor-not-allowed"
                        data-testid="phone-primary-checkbox-primary"
                      />
                      Primary
                    </label>
                    <span className="w-6" />
                  </div>
                  {(formData.additional_phones || []).map((val, idx) => (
                    <div key={`phone-${idx}`} className="flex items-center gap-2" data-testid={`phone-row-${idx}`}>
                      <input
                        type="tel"
                        value={val || ''}
                        onChange={(e) => updateAdditionalPhone(idx, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        placeholder={`Additional mobile ${idx + 1}`}
                        data-testid={`phone-additional-input-${idx}`}
                      />
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 select-none cursor-pointer" title="Mark as Primary">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() => setPrimaryPhoneFromAdditional(idx)}
                          className="w-4 h-4 accent-teal-600 cursor-pointer"
                          data-testid={`phone-primary-checkbox-${idx}`}
                        />
                        Primary
                      </label>
                      <button
                        type="button"
                        onClick={() => removePhoneRow(idx)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        data-testid={`phone-remove-${idx}`}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {((formData.additional_phones || []).length + 1) < MAX_CONTACTS && (
                    <button
                      type="button"
                      onClick={addPhoneRow}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 text-teal-700 hover:bg-teal-50 rounded border border-teal-200"
                      data-testid="phone-add-btn"
                    >
                      <Plus size={12} /> Add Mobile Number ({(formData.additional_phones || []).length + 1}/{MAX_CONTACTS})
                    </button>
                  )}
                </div>
                {fieldErrors.phone && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-phone">
                    <AlertCircle size={12} /> {fieldErrors.phone}
                  </p>
                )}
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2" data-testid="email-row-primary">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="email-input"
                      placeholder="Primary email"
                    />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 select-none" title="Primary Email">
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        className="w-4 h-4 accent-teal-600 cursor-not-allowed"
                        data-testid="email-primary-checkbox-primary"
                      />
                      Primary
                    </label>
                    <span className="w-6" />
                  </div>
                  {(formData.additional_emails || []).map((val, idx) => (
                    <div key={`email-${idx}`} className="flex items-center gap-2" data-testid={`email-row-${idx}`}>
                      <input
                        type="email"
                        value={val || ''}
                        onChange={(e) => updateAdditionalEmail(idx, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        placeholder={`Additional email ${idx + 1}`}
                        data-testid={`email-additional-input-${idx}`}
                      />
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 select-none cursor-pointer" title="Mark as Primary">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() => setPrimaryEmailFromAdditional(idx)}
                          className="w-4 h-4 accent-teal-600 cursor-pointer"
                          data-testid={`email-primary-checkbox-${idx}`}
                        />
                        Primary
                      </label>
                      <button
                        type="button"
                        onClick={() => removeEmailRow(idx)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        data-testid={`email-remove-${idx}`}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {((formData.additional_emails || []).length + 1) < MAX_CONTACTS && (
                    <button
                      type="button"
                      onClick={addEmailRow}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 text-teal-700 hover:bg-teal-50 rounded border border-teal-200"
                      data-testid="email-add-btn"
                    >
                      <Plus size={12} /> Add Email Address ({(formData.additional_emails || []).length + 1}/{MAX_CONTACTS})
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Owner <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="lead_owner"
                  value={formData.lead_owner}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                    fieldErrors.lead_owner ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  data-testid="lead-owner-input"
                />
                {authUser?.role && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5" data-testid="lead-owner-role">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                    {authUser.role}
                  </p>
                )}
                {fieldErrors.lead_owner && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="error-lead_owner">
                    <AlertCircle size={12} /> {fieldErrors.lead_owner}
                  </p>
                )}
              </div>

              <LeadSourceField
                value={formData.source}
                onChange={(val) => {
                  setFormData(prev => ({ ...prev, source: val }));
                  if (submitted || fieldErrors.source) {
                    const error = validateField('source', val);
                    setFieldErrors(prev => {
                      const next = { ...prev };
                      if (error) next.source = error;
                      else delete next.source;
                      return next;
                    });
                  }
                }}
                required
                error={fieldErrors.source}
              />

              {/* iter246 — Closed Won Date (backed by existing lead_date field).
                  During Add Account: defaults to today, editable by any user.
                  During Edit: admin only, non-admins see it read-only.
                  This preserves the historical lead_date semantics exactly. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Closed Won Date</label>
                <input
                  type="date"
                  name="lead_date"
                  value={formData.lead_date ? String(formData.lead_date).split('T')[0] : ''}
                  onChange={handleChange}
                  disabled={!isNewLead && (authUser?.role || '').toLowerCase() !== 'admin'}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                    !isNewLead && (authUser?.role || '').toLowerCase() !== 'admin'
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'border-gray-300'
                  }`}
                  data-testid="closed-won-date-input"
                />
                {!isNewLead && (authUser?.role || '').toLowerCase() !== 'admin' && (
                  <p className="text-xs text-gray-400 mt-1">Only Admin can edit Closed Won Date after account creation.</p>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* Phase 18a — Commercial Details (payment + setup clearing terms) */}
          <CollapsibleSection title="Commercial Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Interested Plan — dynamic dropdown sourced from
                  Settings → Module Settings → Hotel Onboarded → Plan Management.
                  Selecting a plan auto-fills One Time Setup Fee. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interested Plan</label>
                <select
                  name="interested_plan"
                  value={formData.payment_info?.interested_plan || ''}
                  onChange={(e) => {
                    const selectedName = e.target.value;
                    const plan = accountPlans.find(p => p.name === selectedName);
                    setFormData(prev => {
                      const next = { ...prev, payment_info: { ...prev.payment_info, interested_plan: selectedName } };
                      if (plan) {
                        // Auto-fetch price → One Time Setup Fee (field becomes read-only while a plan is selected).
                        next.payment_info.one_time_setup_fee = Number(plan.price);
                      } else if (!selectedName) {
                        // User cleared the plan → re-enable the fee field by blanking it.
                        next.payment_info.one_time_setup_fee = '';
                      }
                      // Re-derive Pending Payment whenever the trio changes.
                      const fst = parseFloat(next.payment_info.finalize_setup_token);
                      const adv = parseFloat(next.payment_info.token_advance);
                      if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                        const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                        next.payment_info.pending_payment = Math.max(0, pending);
                      }
                      return next;
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                  data-testid="interested-plan-select"
                >
                  <option value="">Select Plan</option>
                  {accountPlans.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
                {accountPlans.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No plans configured. Admin can add them in Settings → Module Settings → Accounts → Plan Management.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Completed Date</label>
                <input
                  type="date"
                  value={formData.payment_info?.payment_completed_date || ''}
                  onChange={(e) => handleNestedChange('payment_info', 'payment_completed_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="payment-completed-date-input"
                />
              </div>
              {/* One Time Setup Fee — auto-filled & read-only when a plan is selected. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  One Time Setup Fee
                  {formData.payment_info?.interested_plan && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto-filled</span>
                  )}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  readOnly={!!formData.payment_info?.interested_plan}
                  value={formData.payment_info?.one_time_setup_fee ?? ''}
                  onChange={(e) => {
                    // Editable only when no plan is selected — once a plan is picked
                    // the price always syncs from Plan Management.
                    if (formData.payment_info?.interested_plan) return;
                    const val = e.target.value;
                    handleNestedChange('payment_info', 'one_time_setup_fee', val === '' ? '' : parseFloat(val));
                  }}
                  placeholder={formData.payment_info?.interested_plan ? 'Set from plan' : '0.00'}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                    formData.payment_info?.interested_plan ? 'bg-gray-50 border-gray-200 cursor-not-allowed' : 'border-gray-300'
                  }`}
                  data-testid="one-time-setup-fee-input"
                />
              </div>
              {/* iter246 — "Finalize Setup Token" display renamed to "Finalize Setup".
                  Backend field name (payment_info.finalize_setup_token) is unchanged.
                  Drives both Pending Payment and Discount calculations below. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Finalize Setup</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.payment_info?.finalize_setup_token ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setFormData(prev => {
                      const next = { ...prev, payment_info: { ...prev.payment_info, finalize_setup_token: raw === '' ? '' : parseFloat(raw) } };
                      const fst = parseFloat(next.payment_info.finalize_setup_token);
                      const adv = parseFloat(next.payment_info.token_advance);
                      if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                        const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                        const clamped = Math.max(0, pending);
                        next.payment_info.pending_payment = clamped;
                        // Auto Payment Status — Pending if >0, Completed if 0.
                        // Only flip status when BOTH inputs have been touched
                        // so we don't pre-mark Completed for empty forms.
                        const bothTouched = !Number.isNaN(fst) && !Number.isNaN(adv);
                        if (bothTouched) {
                          next.payment_info.setup_payment_status = clamped > 0 ? 'Pending' : 'Completed';
                        }
                      } else {
                        next.payment_info.pending_payment = '';
                      }
                      return next;
                    });
                  }}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="finalize-setup-token-input"
                />
                {/* Discount % display — RCA-1: prefer the stored
                    `commission_discount_percentage` (new CRM field). Fall back to the
                    calculated value ((Setup Fee − Finalize Setup) / Setup Fee × 100)
                    only when the stored field is empty/null (legacy leads). */}
                {(() => {
                  const stored = formData.payment_info?.commission_discount_percentage;
                  const hasStored = stored !== null && stored !== undefined && stored !== '' && !Number.isNaN(parseFloat(stored));
                  const fee = parseFloat(formData.payment_info?.one_time_setup_fee);
                  const fst = parseFloat(formData.payment_info?.finalize_setup_token);
                  const computed = (!Number.isNaN(fee) && !Number.isNaN(fst) && fee > 0) ? ((fee - fst) / fee) * 100 : null;
                  const disc = hasStored ? parseFloat(stored) : computed;
                  if (disc === null || Number.isNaN(disc)) return null;
                  const tone = disc >= 0 ? 'text-emerald-600' : 'text-red-600';
                  return (
                    <p className={`text-xs mt-1 ${tone}`} data-testid="discount-display">
                      Discount: <span className="font-semibold">{disc.toFixed(2)}%</span>{' '}
                      <span className="text-gray-400">{hasStored ? '(stored)' : '((Setup Fee − Finalize Setup) / Setup Fee × 100)'}</span>
                    </p>
                  );
                })()}
              </div>
              {/* iter246 — Commission Agreed moved to position between Finalize Setup and Token Advance
                  per spec ordering. UI-only reorder, no logic change. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission Agreed (%)</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.payment_info?.commission_agreed ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Soft-clamp to 0..100; allow empty for "unset".
                      if (raw === '') { handleNestedChange('payment_info', 'commission_agreed', ''); return; }
                      const num = parseFloat(raw);
                      if (Number.isNaN(num)) return;
                      handleNestedChange('payment_info', 'commission_agreed', Math.max(0, Math.min(100, num)));
                    }}
                    placeholder="e.g. 5, 10, 12.5"
                    className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="commission-agreed-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Token Advance</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.payment_info?.token_advance ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setFormData(prev => {
                      const next = { ...prev, payment_info: { ...prev.payment_info, token_advance: raw === '' ? '' : parseFloat(raw) } };
                      const fst = parseFloat(next.payment_info.finalize_setup_token);
                      const adv = parseFloat(next.payment_info.token_advance);
                      if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                        const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                        const clamped = Math.max(0, pending);
                        next.payment_info.pending_payment = clamped;
                        // Auto Payment Status mirrors the FST handler above.
                        const bothTouched = !Number.isNaN(fst) && !Number.isNaN(adv);
                        if (bothTouched) {
                          next.payment_info.setup_payment_status = clamped > 0 ? 'Pending' : 'Completed';
                        }
                      } else {
                        next.payment_info.pending_payment = '';
                      }
                      return next;
                    });
                  }}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="token-advance-input"
                />
              </div>
              {/* iter246 — "Pending Payment" display renamed to "Setup Pending".
                  Backend field name (payment_info.pending_payment) is unchanged.
                  Read-only; re-derived live whenever either input changes. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setup Pending
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto-calculated</span>
                </label>
                <input
                  type="number"
                  readOnly
                  value={formData.payment_info?.pending_payment ?? ''}
                  placeholder="Auto: Finalize Setup − Token Advance"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-teal-500 outline-none cursor-not-allowed"
                  data-testid="pending-payment-input"
                />
                <p className="text-xs text-gray-400 mt-1">= Finalize Setup − Token Advance</p>
              </div>
              {/* Setup Payment Status — now auto-derived from Finalize Setup vs Token Advance.
                  Read-only so the computed value can't drift from what the form actually stores. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setup Payment Status
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto</span>
                </label>
                <select
                  value={formData.payment_info?.setup_payment_status || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed"
                  data-testid="setup-payment-status-select"
                  aria-readonly="true"
                >
                  {SETUP_PAYMENT_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Pending if Setup Pending &gt; 0; Completed when zero.</p>
              </div>

              {/* iter246 — Setup Clearing Terms: hidden when Setup Payment Status = Completed.
                  Backend value (payment_info.setup_clearing_terms) is preserved for existing accounts. */}
              {formData.payment_info?.setup_payment_status !== 'Completed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Setup Clearing Terms</label>
                  <select
                    value={formData.payment_info?.setup_clearing_terms || ''}
                    onChange={(e) => handleNestedChange('payment_info', 'setup_clearing_terms', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                    data-testid="setup-clearing-terms-select"
                  >
                    {SETUP_CLEARING_TERMS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">How the setup amount is being cleared.</p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* OTA Information */}
          <CollapsibleSection title="OTA Information">
            <div className="space-y-3">
              {/* iter259 — Checklist UX. Every OTA from the dynamic
                  config is rendered as a checkbox. Ticking an OTA
                  reveals its Status selector (Manage / Self-Manage /
                  Integration) and, when status = Integration, a Hotel
                  ID input. Historical OTAs no longer in the config are
                  surfaced by useOtaOptions (labelled "(removed)"). */}
              {dynamicOtaOptions.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  No OTA options configured. Add OTAs in Settings → Module Settings → OTA Configuration.
                </p>
              )}
              {dynamicOtaOptions.map(otaOpt => {
                const otaEntry = (formData.ota_info || []).find(o => o.ota_name === otaOpt.value);
                const isChecked = !!otaEntry;
                const atLimit = (formData.ota_info || []).length >= MAX_OTA_ENTRIES;
                const disableCheck = !isChecked && atLimit;
                return (
                  <div
                    key={otaOpt.value}
                    className={`p-3 bg-gray-50 rounded-lg border ${isChecked ? 'border-teal-300' : 'border-gray-200'}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <label className="flex items-center gap-2 min-w-[200px] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={disableCheck}
                          onChange={() => toggleOtaByName(otaOpt.value)}
                          className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          data-testid={`ota-checkbox-${otaOpt.value}`}
                        />
                        <span className="text-sm font-medium text-gray-800">{otaOpt.label}</span>
                      </label>
                      {isChecked && (
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">OTA Status</label>
                            <select
                              value={otaEntry.ota_status || ''}
                              onChange={(e) => {
                                const next = e.target.value;
                                // iter285 — Gate "Integrated" behind both
                                // Hotel Link + Hotel ID being filled.
                                if (next === 'Integrated') {
                                  const hid = (otaEntry.hotel_id || '').trim();
                                  const hlink = (otaEntry.hotel_link || '').trim();
                                  if (!hid || !hlink) {
                                    toast.error('Fill Hotel Link and Hotel ID before marking Integrated');
                                    return;
                                  }
                                }
                                updateOtaFieldByName(otaOpt.value, 'ota_status', next);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white text-sm"
                              data-testid={`ota-status-${otaOpt.value}`}
                            >
                              {ACCOUNT_OTA_STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          {(otaEntry.ota_status === 'Integration' || otaEntry.ota_status === 'Integrated') && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Hotel ID</label>
                                <input
                                  type="text"
                                  value={otaEntry.hotel_id || ''}
                                  onChange={(e) => updateOtaFieldByName(otaOpt.value, 'hotel_id', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                                  data-testid={`ota-hotel-id-${otaOpt.value}`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Hotel Link</label>
                                <input
                                  type="url"
                                  value={otaEntry.hotel_link || ''}
                                  onChange={(e) => updateOtaFieldByName(otaOpt.value, 'hotel_link', e.target.value)}
                                  placeholder="https://"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                                  data-testid={`ota-hotel-link-${otaOpt.value}`}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {(formData.ota_info || []).length >= MAX_OTA_ENTRIES && (
                <p className="text-xs text-amber-600">
                  Maximum {MAX_OTA_ENTRIES} OTAs can be selected.
                </p>
              )}

              {/* Phase 19.5 — Standalone "OTA Listing Link" field removed.
                  Per-OTA Listing URLs live inside each OTA row (Name +
                  Status + URL). The lead-level `ota_listing_link` column
                  is still persisted by the backend (set via existing
                  WhatsApp/auto-sync flows) — only the manual edit UI is
                  hidden. No schema or API change. */}
            </div>
          </CollapsibleSection>

          {/* Phase 18a.1 — PEF section. Document Checklist removed per spec.
              Direct upload supported in BOTH create and edit modes:
              - Edit mode: PefPdfCard talks to the backend directly.
              - Create mode: the picked File is staged locally and uploaded
                by the parent after the lead is created. */}
          <CollapsibleSection title="PEF">
            <PefPdfCard
              leadId={lead?.id || null}
              initialData={lead?.documentCollection || {}}
              onStagedFileChange={setStagedPefFile}
            />
          </CollapsibleSection>

          {/* Phase 18b.3 — Dynamic custom fields previously rendered here have
              been moved to Settings → Account Form Builder for configuration.
              The popup intentionally renders ONLY the 5 core sections.
              Existing custom_fields data is preserved and is still visible
              read-only on LeadDetails for transparency. */}
        </form>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
          <button 
            onClick={onClose}
            type="button"
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            data-testid="lead-modal-cancel"
          >
            Cancel
          </button>
          {isNewLead && (
            <button
              onClick={saveDraft}
              type="button"
              disabled={draftStatus === 'saving'}
              className="px-4 py-2 border border-teal-600 text-teal-700 bg-white rounded-lg hover:bg-teal-50 transition-colors font-medium flex items-center disabled:opacity-60"
              data-testid="lead-modal-save-draft"
              title="Save your progress as a private draft. Only you can see it."
            >
              <Cloud size={16} className="mr-2" />
              {draftStatus === 'saving' ? 'Saving Draft…' : 'Save Draft'}
            </button>
          )}
          <button 
            onClick={handleSubmit}
            type="button"
            disabled={isSaving}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm font-medium flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="lead-modal-save"
          >
            {isSaving ? (
              <span className="mr-2 inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Save size={18} className="mr-2" />
            )}
            {isSaving ? 'Saving…' : 'Save Lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
